import type {
  ClientPortal,
  PortalDeliverableApproval,
  PortalInstallmentPayment,
  PortalProject,
  PortalStage,
} from '../types';
import { portalRootDesignFeePaidAppliesToProject } from './portalDesignFeePaid';
import { portalHasModelContent } from './portalStageProgress';
import { resolveDisplayStage } from './portalStageEngine';

/**
 * Public portal proposal availability for a deal tab.
 * Only `proposalBuilders/{dealId}` counts — never a sibling project's slug on the portal row.
 */
export interface PortalDealProposalSnapshot {
  slug: string | null;
  status: string | null;
  isRomPricing: boolean;
}

export function portalDealProposalFromBuilder(
  builder: { slug?: unknown; status?: unknown; isRomPricing?: unknown } | null | undefined,
): PortalDealProposalSnapshot {
  if (!builder) {
    return { slug: null, status: null, isRomPricing: false };
  }
  return {
    slug: typeof builder.slug === 'string' && builder.slug.trim() ? builder.slug.trim() : null,
    status: typeof builder.status === 'string' ? builder.status : 'draft',
    isRomPricing: !!builder.isRomPricing,
  };
}

/** Parse the client-safe installment payment mirror written by the sales-order trigger. */
export function parseInstallmentPayments(raw: unknown): PortalInstallmentPayment[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const toDate = (v: unknown): Date | undefined =>
    v && typeof v === 'object' && 'toDate' in v && typeof (v as { toDate?: () => Date }).toDate === 'function'
      ? (v as { toDate: () => Date }).toDate()
      : v instanceof Date
        ? v
        : undefined;
  const money = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  const rows = raw
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .map(r => {
      const partiallyBilled = r.partiallyBilled === true;
      const structureGroupLabels = Array.isArray(r.structureGroupLabels)
        ? r.structureGroupLabels.map((label) => String(label || '').trim()).filter(Boolean)
        : [];
      return {
        installmentId: String(r.installmentId || ''),
        label: String(r.label || ''),
        amount: Number(r.amount) || 0,
        status: (r.status as PortalInstallmentPayment['status']) || 'not_billed',
        paidAt: toDate(r.paidAt),
        invoicedAt: toDate(r.invoicedAt) || (partiallyBilled ? undefined : toDate(r.paidAt)),
        amountBilled: money(r.amountBilled),
        amountPaid: money(r.amountPaid),
        amountUnbilled: money(r.amountUnbilled),
        partiallyBilled,
        ...(structureGroupLabels.length ? { structureGroupLabels } : {}),
      };
    });
  return rows.length ? rows : undefined;
}

function parseDeliverableApproval(raw: unknown): PortalDeliverableApproval | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const d = raw as Record<string, unknown>;
  const ts = (v: unknown): Date | undefined =>
    v && typeof v === 'object' && 'toDate' in v && typeof (v as { toDate?: () => Date }).toDate === 'function'
      ? (v as { toDate: () => Date }).toDate()
      : undefined;
  const status = d.status === 'approved' ? 'approved' : 'pending';
  return {
    status,
    approvedAt: ts(d.approvedAt),
    approvedByName: String(d.approvedByName || ''),
    approvedByEmail: typeof d.approvedByEmail === 'string' ? d.approvedByEmail : undefined,
    signature: String(d.signature || ''),
    releaseId: typeof d.releaseId === 'string' ? d.releaseId : undefined,
    bundleId: typeof d.bundleId === 'string' ? d.bundleId : undefined,
    releaseVersionIndex:
      typeof d.releaseVersionIndex === 'number' ? d.releaseVersionIndex : undefined,
    assetUrl: typeof d.assetUrl === 'string' ? d.assetUrl : undefined,
  };
}

function parseDeliverableApprovalHistory(raw: unknown): PortalDeliverableApproval[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const parsed: PortalDeliverableApproval[] = [];
  for (let i = 0; i < raw.length; i++) {
    const approval = parseDeliverableApproval(raw[i]);
    if (approval) parsed.push(approval);
  }
  return parsed.length ? parsed : undefined;
}

/** Parse one row from clientPortals.projects.{dealId}. */
export function parsePortalProjectRow(dId: string, pData: Record<string, unknown>): PortalProject {
  const ts = (v: unknown): Date | undefined =>
    v && typeof v === 'object' && 'toDate' in v && typeof (v as { toDate?: () => Date }).toDate === 'function'
      ? (v as { toDate: () => Date }).toDate()
      : undefined;
  return {
    dealId: (pData.dealId as string) || dId,
    projectName: (pData.projectName as string) || '',
    descriptor: (pData.descriptor as string | undefined) || undefined,
    clientLabel: (pData.clientLabel as string | undefined) || undefined,
    projectNumber: (pData.projectNumber as string | undefined) || undefined,
    proposalSlug: pData.proposalSlug as string | undefined,
    engagementLetterId: pData.engagementLetterId as string | undefined,
    engagementLetterSlug: pData.engagementLetterSlug as string | undefined,
    currentStage: (pData.currentStage as PortalStage) || 'design-fee',
    stageHistory: (pData.stageHistory as PortalProject['stageHistory']) || [],
    stageManualOverride: !!pData.stageManualOverride,
    designFeeSkipped: !!pData.designFeeSkipped,
    designFeePaid: !!pData.designFeePaid,
    designFeePaidAt: ts(pData.designFeePaidAt),
    amountPaid: pData.amountPaid as number | undefined,
    installmentPayments: parseInstallmentPayments(pData.installmentPayments),
    selectedServices: pData.selectedServices as string[] | undefined,
    agreementSigned: !!pData.agreementSigned,
    agreementSignedAt: ts(pData.agreementSignedAt),
    agreementSignedBy: pData.agreementSignedBy as string | undefined,
    agreementSignature: pData.agreementSignature as string | undefined,
    agreementServices: pData.agreementServices as PortalProject['agreementServices'],
    agreementTotal: pData.agreementTotal as number | undefined,
    stripeSessionId: pData.stripeSessionId as string | undefined,
    stripeReceiptUrl: pData.stripeReceiptUrl as string | undefined,
    modelUrl: pData.modelUrl as string | undefined,
    modelImageUrl: pData.modelImageUrl as string | undefined,
    modelLoomUrl: pData.modelLoomUrl as string | undefined,
    modelNotes: pData.modelNotes as string | undefined,
    modelStepSkipped: !!pData.modelStepSkipped,
    conceptApprovalFlowDisabled: !!pData.conceptApprovalFlowDisabled,
    plansUrl: pData.plansUrl as string | undefined,
    conceptElevationUrl: pData.conceptElevationUrl as string | undefined,
    conceptElevationSourceReleaseId: pData.conceptElevationSourceReleaseId as string | undefined,
    conceptElevationSourceProjectId: pData.conceptElevationSourceProjectId as string | undefined,
    conceptElevationSourceVersionLabel: pData.conceptElevationSourceVersionLabel as string | undefined,
    plansApproval: parseDeliverableApproval(pData.plansApproval),
    plansApprovalHistory: parseDeliverableApprovalHistory(pData.plansApprovalHistory),
    modelApproval: parseDeliverableApproval(pData.modelApproval),
    modelApprovalHistory: parseDeliverableApprovalHistory(pData.modelApprovalHistory),
    conceptApproval: parseDeliverableApproval(pData.conceptApproval),
    conceptApprovalHistory: parseDeliverableApprovalHistory(pData.conceptApprovalHistory),
    productionStatus: pData.productionStatus as PortalProject['productionStatus'],
    estimatedCompletion: ts(pData.estimatedCompletion),
    productionNotes: pData.productionNotes as string | undefined,
    deliveryDate: ts(pData.deliveryDate),
    deliveryStatus: pData.deliveryStatus as PortalProject['deliveryStatus'],
    completionNotes: pData.completionNotes as string | undefined,
    timelineOverrides: pData.timelineOverrides as PortalProject['timelineOverrides'],
    targetCompletionDate: ts(pData.targetCompletionDate),
    questionnaireStatus: pData.questionnaireStatus as PortalProject['questionnaireStatus'],
    customDesignFeeServices: pData.customDesignFeeServices as PortalProject['customDesignFeeServices'],
    modelWalkthroughEnabled:
      typeof pData.modelWalkthroughEnabled === 'boolean' ? pData.modelWalkthroughEnabled : undefined,
    modelWalkthroughEffective:
      typeof pData.modelWalkthroughEffective === 'boolean' ? pData.modelWalkthroughEffective : undefined,
    inspirationBoardPortalEnabled:
      typeof pData.inspirationBoardPortalEnabled === 'boolean'
        ? pData.inspirationBoardPortalEnabled
        : undefined,
    approvalTrailStructures: Array.isArray(pData.approvalTrailStructures)
      ? (pData.approvalTrailStructures as PortalProject['approvalTrailStructures'])
      : undefined,
    approvalTrailStructureGroups: Array.isArray(pData.approvalTrailStructureGroups)
      ? (pData.approvalTrailStructureGroups as PortalProject['approvalTrailStructureGroups'])
      : undefined,
    approvalTrailGroupingSyncedAt: ts(pData.approvalTrailGroupingSyncedAt),
    createdAt: ts(pData.createdAt) || new Date(),
    updatedAt: ts(pData.updatedAt) || new Date(),
    hiddenFromPortal: !!pData.hiddenFromPortal,
  };
}

/** Merge live Firestore portal fields (e.g. after a Project Hub release). */
export function mergePortalFromFirestore(prev: ClientPortal, data: Record<string, unknown>): ClientPortal {
  const next: ClientPortal = { ...prev };
  if (data.projects && typeof data.projects === 'object') {
    const merged: Record<string, PortalProject> = { ...(prev.projects || {}) };
    for (const [dId, raw] of Object.entries(data.projects as Record<string, unknown>)) {
      if (!raw || typeof raw !== 'object') continue;
      merged[dId] = {
        ...(merged[dId] || parsePortalProjectRow(dId, {})),
        ...parsePortalProjectRow(dId, raw as Record<string, unknown>),
      };
    }
    next.projects = merged;
  }
  if (Array.isArray(data.installmentPayments)) {
    next.installmentPayments = parseInstallmentPayments(data.installmentPayments);
  }
  if (typeof data.currentStage === 'string') next.currentStage = data.currentStage as PortalStage;
  if (typeof data.modelUrl === 'string') next.modelUrl = data.modelUrl;
  if (typeof data.plansUrl === 'string') next.plansUrl = data.plansUrl;
  if (typeof data.modelImageUrl === 'string') next.modelImageUrl = data.modelImageUrl;
  if (typeof data.modelLoomUrl === 'string') next.modelLoomUrl = data.modelLoomUrl;
  if (typeof data.inspirationBoardPortalEnabled === 'boolean') {
    next.inspirationBoardPortalEnabled = data.inspirationBoardPortalEnabled;
  }
  return next;
}

export function sortVisiblePortalProjects(projects: Record<string, PortalProject>): PortalProject[] {
  const projectSortKey = (p: PortalProject): number => {
    const raw = (p.projectNumber || p.projectName || '').toString();
    const m = raw.match(/\d+/);
    return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER;
  };
  return Object.values(projects)
    .filter(p => !p.hiddenFromPortal)
    .sort((a, b) => {
      const diff = projectSortKey(a) - projectSortKey(b);
      if (diff !== 0) return diff;
      return (a.projectName || '').localeCompare(b.projectName || '');
    });
}

export type LeadDealPortalSlice = {
  id: string;
  projectName?: string;
  descriptor?: string;
  projectNumber?: string;
  clientLabel?: string;
  title?: string;
};

function resolveDealDisplayName(deal: LeadDealPortalSlice): string {
  return (
    deal.projectName ||
    (deal.title?.includes(' - ') ? deal.title.split(' - ').slice(1).join(' - ') : deal.title) ||
    (deal.descriptor || '').trim() ||
    'Project'
  );
}

export function buildPortalProjectFromDeal(deal: LeadDealPortalSlice, now = new Date()): PortalProject {
  const projectName = resolveDealDisplayName(deal);
  return {
    dealId: deal.id,
    projectName,
    descriptor: (deal.descriptor || '').trim() || projectName,
    clientLabel: (deal.clientLabel || '').trim() || '',
    projectNumber: (deal.projectNumber || '').trim() || '',
    currentStage: 'design-fee',
    stageHistory: [{ stage: 'design-fee', enteredAt: now }],
    designFeeSkipped: false,
    hiddenFromPortal: false,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Keep only projects whose deals belong to this lead; add rows for CRM deals
 * missing from the portal map (e.g. newly created Anchorite).
 */
export function reconcilePortalProjectsForLead(
  projects: Record<string, PortalProject>,
  leadDeals: LeadDealPortalSlice[],
): Record<string, PortalProject> {
  const next: Record<string, PortalProject> = {};
  for (const deal of leadDeals) {
    if (!deal.id) continue;
    const existing = projects[deal.id];
    const projectName = resolveDealDisplayName(deal);
    if (existing) {
      next[deal.id] = {
        ...existing,
        projectName: projectName || existing.projectName,
        descriptor: (deal.descriptor || '').trim() || existing.descriptor || existing.projectName,
        clientLabel: (deal.clientLabel || '').trim() || existing.clientLabel,
        projectNumber: (deal.projectNumber || '').trim() || existing.projectNumber,
      };
    } else {
      next[deal.id] = buildPortalProjectFromDeal(deal);
    }
  }
  return next;
}

export type PortalProjectReconciliationDiff = {
  staleIds: string[];
  missingIds: string[];
};

export function diffPortalProjectReconciliation(
  raw: Record<string, PortalProject>,
  reconciled: Record<string, PortalProject>,
): PortalProjectReconciliationDiff {
  const reconciledIds = new Set(Object.keys(reconciled));
  const rawIds = new Set(Object.keys(raw));
  return {
    staleIds: Array.from(rawIds).filter(id => !reconciledIds.has(id)),
    missingIds: Array.from(reconciledIds).filter(id => !rawIds.has(id)),
  };
}

function portalProjectRowToFirestore(row: PortalProject, now: import('firebase/firestore').Timestamp) {
  return {
    dealId: row.dealId,
    projectName: row.projectName,
    descriptor: row.descriptor || '',
    clientLabel: row.clientLabel || '',
    projectNumber: row.projectNumber || '',
    currentStage: row.currentStage,
    stageHistory: [{ stage: 'design-fee' as PortalStage, enteredAt: now }],
    designFeeSkipped: row.designFeeSkipped ?? false,
    hiddenFromPortal: row.hiddenFromPortal ?? false,
    createdAt: now,
    updatedAt: now,
  };
}

/** Persist stale-key deletes and missing rows after reconciliation (client or staff). */
export async function persistPortalProjectReconciliation(
  portalId: string,
  raw: Record<string, PortalProject>,
  reconciled: Record<string, PortalProject>,
  leadDeals: LeadDealPortalSlice[],
): Promise<boolean> {
  const { staleIds, missingIds } = diffPortalProjectReconciliation(raw, reconciled);
  if (staleIds.length === 0 && missingIds.length === 0) return false;

  const { doc, updateDoc, deleteField, Timestamp } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  const now = Timestamp.now();
  const updates: Record<string, any> = { updatedAt: now };

  for (const id of staleIds) {
    updates[`projects.${id}`] = deleteField();
  }
  for (const id of missingIds) {
    const deal = leadDeals.find(d => d.id === id);
    if (!deal) continue;
    const row = buildPortalProjectFromDeal(deal);
    updates[`projects.${id}`] = portalProjectRowToFirestore(row, now);
  }

  try {
    await updateDoc(doc(db, 'clientPortals', portalId), updates);
    console.log('[Portal] Persisted project reconciliation', { portalId, staleIds, missingIds });
    return true;
  } catch (err) {
    console.warn('[Portal] Could not persist project reconciliation:', err);
    return false;
  }
}

function portalProjectDealIds(projects: Record<string, PortalProject>): string[] {
  return Array.from(new Set(Object.keys(projects).map(id => id.trim()).filter(Boolean)));
}

async function loadLeadDealsForPortal(
  leadId: string,
  knownDealIds: string[] = [],
): Promise<LeadDealPortalSlice[]> {
  const { collection, doc, getDoc, getDocs, query, where } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  try {
    const dealsSnap = await getDocs(
      query(collection(db, 'enhancedDeals'), where('leadId', '==', leadId)),
    );
    return dealsSnap.docs.map(d => ({
      id: d.id,
      ...(d.data() as Omit<LeadDealPortalSlice, 'id'>),
    }));
  } catch {
    // Public portal cannot list the deal pipeline — load only deal docs already on the portal.
    const slices: LeadDealPortalSlice[] = [];
    await Promise.all(
      Array.from(new Set(knownDealIds.filter(Boolean))).map(async (id) => {
        const snap = await getDoc(doc(db, 'enhancedDeals', id));
        if (!snap.exists()) return;
        const data = snap.data() as Omit<LeadDealPortalSlice, 'id'> & { leadId?: string };
        if (data.leadId && data.leadId !== leadId) return;
        slices.push({ id: snap.id, ...data });
      }),
    );
    return slices;
  }
}

/** Load enhancedDeals for a lead and reconcile portal.projects to match. */
export async function reconcilePortalProjectsFromLead(
  projects: Record<string, PortalProject>,
  leadId: string | undefined,
): Promise<Record<string, PortalProject>> {
  if (!leadId?.trim()) return projects;

  try {
    const leadDeals = await loadLeadDealsForPortal(leadId, portalProjectDealIds(projects));
    return reconcilePortalProjectsForLead(projects, leadDeals);
  } catch (err) {
    console.warn('[Portal] Could not reconcile projects for lead:', err);
    return projects;
  }
}

/** Reconcile portal.projects and write stale-key deletes / missing rows to Firestore. */
export async function reconcileAndPersistPortalProjects(
  portalId: string,
  projects: Record<string, PortalProject>,
  leadId: string | undefined,
): Promise<Record<string, PortalProject>> {
  if (!leadId?.trim()) return projects;

  try {
    const leadDeals = await loadLeadDealsForPortal(leadId, portalProjectDealIds(projects));
    const reconciled = reconcilePortalProjectsForLead(projects, leadDeals);
    await persistPortalProjectReconciliation(portalId, projects, reconciled, leadDeals);
    return reconciled;
  } catch (err) {
    console.warn('[Portal] Could not reconcile/persist projects for lead:', err);
    return projects;
  }
}

/** @deprecated Use reconcilePortalProjectsFromLead — kept for call-site compatibility. */
export async function filterOrphanPortalProjects(
  projects: Record<string, PortalProject>,
  leadId: string | undefined,
  _readDealExists?: (dealId: string) => Promise<boolean>,
): Promise<Record<string, PortalProject>> {
  return reconcilePortalProjectsFromLead(projects, leadId);
}

export function isMultiProjectPortal(portal: ClientPortal): boolean {
  return !!portal.projects && Object.keys(portal.projects).length > 1;
}

/**
 * Only the designated primary deal (or legacy single-project portals) may inherit
 * root-level portal fields. When `portal.dealId` is unset on a multi-project portal,
 * siblings must not all inherit Oratory's model, notes, etc.
 */
export function isPrimaryPortalProject(portal: ClientPortal, dealId: string): boolean {
  if (!dealId) return true;
  if (!isMultiProjectPortal(portal)) {
    return !portal.dealId || portal.dealId === dealId;
  }
  return !!portal.dealId && portal.dealId === dealId;
}

/** Per-project asset fields — on multi-project portals, never fall back to legacy root fields. */
function projectAssetField(
  portal: ClientPortal,
  isPrimary: boolean,
  projectValue: string | undefined,
  rootValue: string | undefined,
): string | undefined {
  const trimmed = projectValue?.trim();
  if (trimmed) return trimmed;
  if (isMultiProjectPortal(portal)) return undefined;
  if (isPrimary) return rootValue?.trim() || undefined;
  return undefined;
}

/**
 * Build a per-project client-portal view. Sibling projects must never inherit the
 * root portal's stage, model URL, or proposal slug — that caused multi-project
 * portals to look "reset" when switching tabs.
 */
export function buildEffectivePortalProject(portal: ClientPortal, dealId: string): ClientPortal {
  const proj = portal.projects?.[dealId];
  const isPrimary = isPrimaryPortalProject(portal, dealId);

  if (!proj) {
    if (isPrimary && !isMultiProjectPortal(portal)) return portal;
    return {
      ...portal,
      dealId,
      modelUrl: undefined,
      modelImageUrl: undefined,
      modelLoomUrl: undefined,
      modelNotes: undefined,
      plansUrl: undefined,
      currentStage: 'design-fee',
      designFeeSkipped: false,
      designFeePaid: false,
      agreementSigned: false,
      questionnaireStatus: 'not-started',
    } as ClientPortal;
  }

  const healPayment =
    !proj.designFeePaid &&
    portalRootDesignFeePaidAppliesToProject(portal as unknown as Record<string, unknown>, dealId);

  return {
    ...portal,
    dealId,
    currentStage: (proj.currentStage || (isPrimary ? portal.currentStage : 'design-fee')) as PortalStage,
    stageHistory: proj.stageHistory?.length ? proj.stageHistory : isPrimary ? portal.stageHistory : [],
    stageManualOverride: proj.stageManualOverride ?? (isPrimary ? portal.stageManualOverride : false),
    designFeeSkipped: proj.designFeeSkipped ?? (isPrimary ? portal.designFeeSkipped : false),
    designFeePaid: !!(proj.designFeePaid || healPayment),
    designFeePaidAt: proj.designFeePaidAt || (healPayment ? portal.designFeePaidAt : undefined),
    amountPaid: proj.amountPaid ?? (healPayment ? portal.amountPaid : undefined),
    installmentPayments: proj.installmentPayments ?? (isPrimary ? portal.installmentPayments : undefined),
    statement: proj.statement ?? (isPrimary ? portal.statement : undefined),
    bond: proj.bond ?? (isPrimary ? portal.bond : undefined),
    selectedServices: proj.selectedServices ?? (isPrimary ? portal.selectedServices : undefined),
    agreementSigned: proj.agreementSigned ?? (isPrimary ? portal.agreementSigned : false),
    agreementSignedAt: proj.agreementSignedAt ?? (isPrimary ? portal.agreementSignedAt : undefined),
    agreementSignedBy: proj.agreementSignedBy ?? (isPrimary ? portal.agreementSignedBy : undefined),
    agreementSignature: proj.agreementSignature ?? (isPrimary ? portal.agreementSignature : undefined),
    agreementServices: proj.agreementServices ?? (isPrimary ? portal.agreementServices : undefined),
    agreementTotal: proj.agreementTotal ?? (isPrimary ? portal.agreementTotal : undefined),
    stripeSessionId: proj.stripeSessionId || (healPayment ? portal.stripeSessionId : undefined),
    stripeReceiptUrl: proj.stripeReceiptUrl || (healPayment ? portal.stripeReceiptUrl : undefined),
    modelUrl: projectAssetField(portal, isPrimary, proj.modelUrl, portal.modelUrl),
    modelImageUrl: projectAssetField(portal, isPrimary, proj.modelImageUrl, portal.modelImageUrl),
    modelLoomUrl: projectAssetField(portal, isPrimary, proj.modelLoomUrl, portal.modelLoomUrl),
    modelNotes: projectAssetField(portal, isPrimary, proj.modelNotes, portal.modelNotes),
    modelStepSkipped: proj.modelStepSkipped ?? (isPrimary ? portal.modelStepSkipped : false),
    conceptApprovalFlowDisabled:
      proj.conceptApprovalFlowDisabled ?? (isPrimary ? portal.conceptApprovalFlowDisabled : false),
    plansUrl: projectAssetField(portal, isPrimary, proj.plansUrl, portal.plansUrl),
    conceptElevationUrl: projectAssetField(
      portal,
      isPrimary,
      proj.conceptElevationUrl,
      (portal as ClientPortal & { conceptElevationUrl?: string }).conceptElevationUrl,
    ),
    plansApproval: proj.plansApproval ?? (isPrimary ? (portal as ClientPortal).plansApproval : undefined),
    plansApprovalHistory:
      proj.plansApprovalHistory ?? (isPrimary ? (portal as ClientPortal).plansApprovalHistory : undefined),
    modelApproval: proj.modelApproval ?? (isPrimary ? (portal as ClientPortal).modelApproval : undefined),
    modelApprovalHistory:
      proj.modelApprovalHistory ?? (isPrimary ? (portal as ClientPortal).modelApprovalHistory : undefined),
    conceptApproval: proj.conceptApproval ?? (isPrimary ? (portal as ClientPortal).conceptApproval : undefined),
    conceptApprovalHistory:
      proj.conceptApprovalHistory ?? (isPrimary ? (portal as ClientPortal).conceptApprovalHistory : undefined),
    proposalSlug: proj.proposalSlug ?? (isPrimary ? portal.proposalSlug : undefined),
    engagementLetterSlug: proj.engagementLetterSlug ?? (isPrimary ? portal.engagementLetterSlug : undefined),
    engagementLetterId: proj.engagementLetterId ?? (isPrimary ? portal.engagementLetterId : undefined),
    productionStatus: proj.productionStatus ?? (isPrimary ? portal.productionStatus : undefined),
    estimatedCompletion: proj.estimatedCompletion ?? (isPrimary ? portal.estimatedCompletion : undefined),
    productionNotes: proj.productionNotes ?? (isPrimary ? portal.productionNotes : undefined),
    deliveryDate: proj.deliveryDate ?? (isPrimary ? portal.deliveryDate : undefined),
    deliveryStatus: proj.deliveryStatus ?? (isPrimary ? portal.deliveryStatus : undefined),
    completionNotes: proj.completionNotes ?? (isPrimary ? portal.completionNotes : undefined),
    questionnaireStatus: proj.questionnaireStatus ?? (isPrimary ? portal.questionnaireStatus : 'not-started'),
    modelWalkthroughEnabled: proj.modelWalkthroughEnabled,
    modelWalkthroughEffective: proj.modelWalkthroughEffective,
    inspirationBoardPortalEnabled:
      proj.inspirationBoardPortalEnabled ??
      (isPrimary ? portal.inspirationBoardPortalEnabled : false),
  } as ClientPortal;
}

export function resolvePortalDisplayStage(
  stored: PortalStage,
  opts: {
    project: Parameters<typeof portalHasModelContent>[0];
    proposalStatus?: string | null;
    dealOutcome?: 'won' | 'lost' | 'active';
    /** Manual retreat pin — suppresses all auto-bumps (see portalStageEngine). */
    manualOverride?: boolean;
  },
): PortalStage {
  return resolveDisplayStage({
    storedStage: stored,
    manualOverride: opts.manualOverride,
    hasModelContent: portalHasModelContent(opts.project),
    modelStepSkipped: !!opts.project?.modelStepSkipped,
    proposalStatus: opts.proposalStatus,
    dealOutcome: opts.dealOutcome,
  });
}

/** Apply release-row fallback URL and compute the display stage for timeline UI. */
export function enrichPortalWithReleases(
  portal: ClientPortal,
  opts: {
    releasedModelUrl?: string;
    releaseDealId?: string | null;
    proposalStatus?: string | null;
    dealOutcome?: 'won' | 'lost' | 'active';
  },
): ClientPortal {
  const releaseUrl = opts.releasedModelUrl?.trim() || undefined;
  const modelUrl = portal.modelUrl?.trim() || releaseUrl || undefined;
  const withModel: ClientPortal = {
    ...portal,
    modelUrl,
    modelImageUrl: portal.modelImageUrl?.trim() || undefined,
    modelLoomUrl: portal.modelLoomUrl?.trim() || undefined,
    modelNotes: portal.modelNotes?.trim() || undefined,
  };
  const displayStage = resolvePortalDisplayStage(withModel.currentStage as PortalStage, {
    project: withModel,
    proposalStatus: opts.proposalStatus,
    dealOutcome: opts.dealOutcome,
    manualOverride: withModel.stageManualOverride,
  });
  if (
    displayStage === withModel.currentStage &&
    withModel.modelUrl === (portal.modelUrl?.trim() || undefined) &&
    withModel.modelImageUrl === (portal.modelImageUrl?.trim() || undefined) &&
    withModel.modelLoomUrl === (portal.modelLoomUrl?.trim() || undefined)
  ) {
    return portal;
  }
  return { ...withModel, currentStage: displayStage } as ClientPortal;
}
